import { useState } from 'react';
import mascot320 from '@/assets/emjae-mascot-320.webp';
import mascot96 from '@/assets/emjae-mascot-96.webp';
import { cn } from '@/lib/utils';
import type { CentralChatMessage, TurnOutcome } from './types';

export type EmjaeMascotSize = 'avatar' | 'header' | 'hero' | 'launcher';

/** Art slots. Today every pose is the still image plus an existing CSS motion
 *  class; a later sprite sheet per pose drops in by adding the asset and one CSS
 *  rule keyed on `[data-pose]`. */
export type EmjaeMascotPose = 'idle' | 'wave' | 'talking' | 'thinking' | 'happy';

/** One source of truth for both surfaces. The Arrow.js surface builds its own
 *  <img> from these so the two cannot drift. */
export const EMJAE_MASCOT_SIZES: Record<EmjaeMascotSize, { px: number; src: string; box: string }> = {
  avatar: { px: 36, src: mascot96, box: 'size-9' },
  header: { px: 32, src: mascot96, box: 'size-8' },
  hero: { px: 140, src: mascot320, box: 'size-[140px]' },
  // The corner badger. 68px desktop (px below), 52px under md; the box class and
  // the width/height attribute (EmjaeMascot `px`) say the same thing.
  launcher: { px: 68, src: mascot320, box: 'size-[52px] md:size-[68px]' },
};

export type EmjaeMascotMotion = 'float' | 'bounce' | 'wiggle' | 'sway' | 'wave';
type MotionList = readonly (EmjaeMascotMotion | false | null | undefined)[];

/** Which sizes animate as a sprite, and from which sheet. Avatars stay a still image so a
 *  long conversation stays calm. */
export const SPRITE_SHEET: Partial<Record<EmjaeMascotSize, '112' | '64'>> = {
  launcher: '112',
  hero: '112',
  header: '64',
};

/** A transparent pixel: the sprite is the element's background, and the still image must not
 *  paint over it. */
export const SPRITE_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

/** The sprite already is the wave, the hop, the sway and the wiggle, so those CSS motions
 *  are dropped while it is on (no double animation). The float stays, except that it
 *  gives way on the launcher while a wave or a hop plays. */
export function motionUnderSprite(size: EmjaeMascotSize, pose: EmjaeMascotPose, motion: MotionList): MotionList {
  return motion.filter((m) => {
    if (m === 'wave' || m === 'bounce' || m === 'sway' || m === 'wiggle') return false;
    if (m === 'float' && size === 'launcher' && pose !== 'idle') return false;
    return true;
  });
}

export function emjaeMascotClass(
  size: EmjaeMascotSize,
  motion: MotionList = [],
  sprite?: { pose: EmjaeMascotPose; sheet?: '112' | '64' },
  /** Extra classes after the size box (merged, so `size-9` wins over the header's `size-8`). */
  extra?: string,
): string {
  const sheet = sprite?.sheet ?? SPRITE_SHEET[size];
  const sprites = sprite && sheet ? ['emjae-sprite', `emjae-sprite-${sprite.pose}`, `emjae-sprite-${sheet}`] : [];
  const kept = sprite ? motionUnderSprite(size, sprite.pose, motion) : motion;
  return cn(
    'shrink-0 select-none',
    // The launcher is the badger itself, not a face in a disc.
    size === 'launcher' ? 'object-contain drop-shadow-lg' : 'rounded-full bg-paper-2 object-cover',
    EMJAE_MASCOT_SIZES[size].box,
    ...sprites,
    ...kept.map((m) => (m ? `emjae-mascot-${m}` : false)),
    extra,
  );
}

// A reply earns the bounce when the turn completed ('done', or no outcome
// recorded) or parked for the person to act ('awaiting', a card is showing).
// 'error', 'stopped' (the person pressed Stop) and 'capped' (cut off by the turn
// limit) are partial or failed, so nothing celebrates them. TurnOutcome in types.ts.
const BOUNCE_OUTCOMES: ReadonlySet<TurnOutcome | undefined> = new Set<TurnOutcome | undefined>([
  undefined,
  'done',
  'awaiting',
]);

/** A finished reply earns the happy pose: non-blank assistant text last, outcome done / awaiting / none. */
export function replyCelebrates(messages: readonly CentralChatMessage[]): boolean {
  const last = messages.at(-1);
  return !!last && last.role === 'assistant' && !!last.content.trim() && BOUNCE_OUTCOMES.has(last.traceOutcome);
}

interface MascotMotionInput {
  isStreaming: boolean;
  messages: readonly CentralChatMessage[];
  bubbleOpen: boolean;
  openNonce: number;
}

/**
 * When the one-shots fire.
 *  - bounce: isStreaming true->false with a non-blank assistant reply last whose
 *    outcome is done / awaiting (see BOUNCE_OUTCOMES). The empty placeholder (streaming starts), a history load (messages
 *    replaced, never streaming) and the first render cannot satisfy it.
 *  - wiggle: the panel was opened. The bubble unmounts the body when closed and
 *    the rail swaps layouts, so the body usually mounts AT the open. The runtime's
 *    openNonce (bumped by openBubble and openIssueChat, 0 at page load) is what
 *    tells that apart from a reload, where bubbleOpen is persisted true but the
 *    nonce is 0. So: mounted with a nonce above 0 = just opened; already mounted =
 *    the nonce moved or bubbleOpen went false->true. A rare remount at an unchanged
 *    nonce (rail narrow/desktop swap) replays one 600ms wiggle, which is harmless.
 */
export function useEmjaeMascotMotion({ isStreaming, messages, bubbleOpen, openNonce }: MascotMotionInput) {
  // Both triggers compare against the previous render's values and set state
  // while rendering (React's "adjust state on a prop change" pattern), so no
  // effect has to set state and a transition is seen on the render it happens.
  const [bounceId, setBounceId] = useState<string | null>(null);
  // The header's happy pose has its own flag: it ends on the header's own animationend,
  // which must not cut the newest avatar's bounce short.
  const [happy, setHappy] = useState(false);
  const [wasStreaming, setWasStreaming] = useState(isStreaming);
  if (wasStreaming !== isStreaming) {
    setWasStreaming(isStreaming);
    if (isStreaming) {
      setBounceId(null);
      setHappy(false);
    } else {
      const last = messages.at(-1);
      if (last && replyCelebrates(messages)) {
        setBounceId(last.id);
        setHappy(true);
      }
    }
  }

  // Mounting with a nonce above 0 is "just opened"; mounting with 0 is a reload.
  const [wiggle, setWiggle] = useState(bubbleOpen && openNonce > 0);
  const [seenOpen, setSeenOpen] = useState({ open: bubbleOpen, nonce: openNonce });
  if (seenOpen.open !== bubbleOpen || seenOpen.nonce !== openNonce) {
    setSeenOpen({ open: bubbleOpen, nonce: openNonce });
    if (bubbleOpen && (!seenOpen.open || seenOpen.nonce !== openNonce)) setWiggle(true);
  }

  return {
    bounceId,
    wiggle,
    happy,
    clearBounce: () => setBounceId(null),
    clearWiggle: () => setWiggle(false),
    clearHappy: () => setHappy(false),
  };
}

/**
 * The mascot's pose from the turn. Work wins: while a reply is being made it THINKS from the
 * moment the message is sent until the first text arrives (and whenever a tool is running inside
 * a live stream; a running entry with no stream is a stale trace, not work), then TALKS while the
 * text arrives. Otherwise the one-shots: happy when a reply just finished well, wave when the
 * panel was just opened. Else idle.
 */
export function headerPose(input: {
  isStreaming: boolean;
  toolRunning: boolean;
  /** The last assistant message already has text (the first chunk has arrived). */
  replyStarted: boolean;
  happy: boolean;
  wave: boolean;
}): EmjaeMascotPose {
  if (input.isStreaming) return input.toolRunning || !input.replyStarted ? 'thinking' : 'talking';
  if (input.happy) return 'happy';
  if (input.wave) return 'wave';
  return 'idle';
}

/** True when the last assistant message has non-blank text (the reply's first chunk is in). */
export function replyHasText(messages: readonly CentralChatMessage[]): boolean {
  const last = messages.at(-1);
  return !!last && last.role === 'assistant' && last.content.trim().length > 0;
}

export function prefersReducedMotion(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Full idle sprite cycles before the idle mascot waves: idle, idle, wave on the composer; the
 *  top-bar badger is gentler (about every 20 s at 5.75 s a cycle). */
export const IDLE_CYCLES_BEFORE_WAVE = { composer: 2, badger: 3 } as const;

export interface IdleCycle {
  /** Full idle cycles counted since the last wave or interruption. */
  count: number;
  /** The wave is playing; its animationend ends it. */
  waving: boolean;
}
export const IDLE_CYCLE_START: IdleCycle = { count: 0, waving: false };

/** One idle sprite cycle finished (`animationiteration`). Counted by events, not timers. */
export function cycleOnIteration(state: IdleCycle, every: number): IdleCycle {
  if (state.waving) return state;
  return state.count + 1 >= every ? { count: 0, waving: true } : { count: state.count + 1, waving: false };
}

/** The mascot's animation ended: that ends a cycle wave and starts the count again. */
export function cycleOnEnd(state: IdleCycle): IdleCycle {
  return state.waving ? IDLE_CYCLE_START : state;
}

/**
 * The idle cycle of one mascot: while `resting` (its base pose is idle) it alternates idle and
 * wave. Any other pose resets the count. Under reduced motion it never cycles.
 */
export function useIdleCycle(resting: boolean, every: number) {
  const [state, setState] = useState<IdleCycle>(IDLE_CYCLE_START);
  // Adjust state while rendering (no effect sets state): work or a one-shot interrupts the cycle.
  if (!resting && (state.count > 0 || state.waving)) setState(IDLE_CYCLE_START);
  return {
    waving: resting && state.waving,
    onIteration: () => {
      if (resting && !prefersReducedMotion()) setState((previous) => cycleOnIteration(previous, every));
    },
    onEnd: () => setState(cycleOnEnd),
  };
}
